import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Profile.css';
import { authButtonSmallStyle } from '../constants/authStyles';

const POLL_MS = 5000;
const POLL_FAST_MS = 1500;
const AUTO_HIDE_MS = 12000;
const DISCOVERY_HIDE_MS = 3000; // "kilidi açıldı" bildirimleri kısa kalsın

/**
 * Her oyun ekranında arkadaş bildirimleri: gelen savaş istekleri (Kabul Et / Reddet),
 * yeni arkadaşlık istekleri ve gönderilen savaş isteklerinin sonucu. Sunucuyu kısa
 * aralıklarla yoklar; oyuncu çevrimdışıyken gelen istekler sunucuda bekler, giriş
 * yapınca burada görünür. Yoklama oyuncuyu çevrimiçi olarak da işaretler.
 */
const FriendNotifier = ({ username }) => {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  const location = useLocation();
  const [toasts, setToasts] = useState([]);
  // Keşif bildirimleri sırayla, tek tek gösterilir.
  const [discs, setDiscs] = useState([]);
  const [readyMatch, setReadyMatch] = useState('');
  const dismissed = useRef(new Set());
  const busy = useRef(false);
  const locRef = useRef(location);
  const autoRef = useRef('');
  const fastRef = useRef(false);
  locRef.current = location;

  const enterFight = useCallback((matchId, automatic) => {
    if (!matchId) return;
    const loc = locRef.current;
    if (loc.pathname === '/battle' && loc.state?.arenaOnlineMatch?.matchId === matchId) return;
    if (automatic && autoRef.current === matchId) return;
    if (automatic) autoRef.current = matchId;
    navigate('/battle', { state: { arenaOnlineMatch: { matchId, t: Date.now() } } });
  }, [navigate]);

  const push = useCallback((toast) => {
    setToasts((list) => (list.some((x) => x.key === toast.key) ? list : [...list, toast].slice(-4)));
    if (!toast.sticky) {
      setTimeout(() => setToasts((list) => list.filter((x) => x.key !== toast.key)), toast.ttl || AUTO_HIDE_MS);
    }
  }, []);

  const close = useCallback((key) => {
    dismissed.current.add(key);
    setToasts((list) => list.filter((x) => x.key !== key));
    setDiscs((list) => list.filter((x) => x.key !== key));
  }, []);

  const currentDiscKey = discs[0]?.key;
  useEffect(() => {
    if (!currentDiscKey) return undefined;
    const timer = setTimeout(() => setDiscs((list) => list.filter((x) => x.key !== currentDiscKey)), DISCOVERY_HIDE_MS);
    return () => clearTimeout(timer);
  }, [currentDiscKey]);

  const openReplay = useCallback(async (fightId) => {
    try {
      const res = await axios.get(`/api/arena/offline/fight?username=${encodeURIComponent(username)}&id=${fightId}`);
      if (res.data?.success && res.data.fight) {
        navigate('/battle', { state: { arenaOfflineFight: res.data.fight } });
      }
    } catch (e) { /* yoksay */ }
  }, [navigate, username]);

  const poll = useCallback(async () => {
    if (busy.current || document.hidden) return;
    busy.current = true;
    try {
      const res = await axios.get('/api/friends/notifications');
      const d = res.data;
      if (!d?.success) return;
      const liveIncoming = new Set();
      (d.incomingBattles || []).forEach((b) => {
        const key = `battle-${b.id}`;
        liveIncoming.add(key);
        if (dismissed.current.has(key)) return;
        push({ key, sticky: true, type: 'incoming', battle: b });
      });
      // Süresi dolan / geri çekilen istek ekrandan kalkar.
      setToasts((list) => list.filter((x) => x.type !== 'incoming' || liveIncoming.has(x.key)));
      const matchId = d.activeMatchId || '';
      setReadyMatch(matchId);
      if (!matchId) autoRef.current = '';
      const here = locRef.current.pathname === '/battle'
        && locRef.current.state?.arenaOnlineMatch?.matchId === matchId;
      fastRef.current = (d.outgoingBattles || []).length > 0 || (Boolean(matchId) && !here);
      if (matchId) enterFight(matchId, true);
      (d.battleResults || []).forEach((b) => {
        const key = `result-${b.id}`;
        if (b.status === 'accepted' && b.mode === 'online' && b.matchId) {
          enterFight(b.matchId, true);
          return;
        }
        push({ key, type: 'result', battle: b });
      });
      (d.discoveries || []).forEach((x, i) => {
        const disc = { key: `disc-${x.kind}-${x.name}-${Date.now()}-${i}`, type: 'discovery', disc: x };
        setDiscs((list) => [...list, disc].slice(-30));
      });
      (d.newFriendRequests || []).forEach((name) => {
        push({ key: `friend-${name}-${Date.now()}`, type: 'friend', name });
      });
    } catch (e) {
      /* ağ hatası: bir sonraki yoklamada tekrar denenir */
    } finally {
      busy.current = false;
    }
  }, [push, enterFight]);

  useEffect(() => {
    if (!username) return undefined;
    let timer = 0;
    let stop = false;
    const loop = async () => {
      await poll();
      if (stop) return;
      timer = window.setTimeout(loop, fastRef.current ? POLL_FAST_MS : POLL_MS);
    };
    loop();
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [username, poll]);

  const respond = async (toast, accept) => {
    const b = toast.battle;
    setToasts((list) => list.map((x) => (x.key === toast.key ? { ...x, pending: true, error: '' } : x)));
    try {
      const res = await axios.post('/api/friends/battle/respond', { id: b.id, accept });
      const d = res.data || {};
      if (!d.success) {
        setToasts((list) => list.map((x) => (x.key === toast.key ? { ...x, pending: false, error: d.message || t('actionFailed') } : x)));
        return;
      }
      close(toast.key);
      if (!accept) return;
      if (d.matchId) enterFight(d.matchId, true);
      else if (d.fightId) openReplay(d.fightId);
    } catch (e) {
      setToasts((list) => list.map((x) => (x.key === toast.key ? { ...x, pending: false, error: t('actionFailed') } : x)));
    }
  };

  const inReadyMatch = Boolean(readyMatch)
    && location.pathname === '/battle'
    && location.state?.arenaOnlineMatch?.matchId === readyMatch;
  const showEnter = Boolean(readyMatch) && !inReadyMatch;
  if (!toasts.length && !discs.length && !showEnter) return null;
  const modeText = (m) => (m === 'online' ? t('battleOnline') : t('battleOffline'));

  return (
    <div className="friend-toasts" role="region" aria-live="polite">
      {showEnter && (
        <div className="friend-toast friend-toast--enter">
          <p className="friend-toast-text">{t('enterBattleReady')}</p>
          <div className="friend-toast-actions">
            <button
              type="button"
              className="auth-button auth-button--compact"
              style={authButtonSmallStyle}
              onClick={() => enterFight(readyMatch, false)}
            >
              {t('enterBattle')}
            </button>
          </div>
        </div>
      )}
      {(discs.length ? [...toasts, discs[0]] : toasts).map((toast) => (
        <div key={toast.key} className={`friend-toast${toast.type === 'discovery' ? ' friend-toast--disc' : ''}`}>
          <button type="button" className="friend-toast-close" aria-label={t('close')} data-silent-click="true" onClick={() => close(toast.key)}>×</button>
          {toast.type === 'incoming' && (
            <>
              <p className="friend-toast-text">
                <strong>{toast.battle.from}</strong> {t('toastBattleInvite', { mode: modeText(toast.battle.mode) })}
              </p>
              {toast.error ? <p className="friend-toast-text profile-loss">{toast.error}</p> : null}
              <div className="friend-toast-actions">
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} disabled={toast.pending} onClick={() => respond(toast, false)}>{t('reject')}</button>
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} disabled={toast.pending} onClick={() => respond(toast, true)}>{t('accept')}</button>
              </div>
            </>
          )}
          {toast.type === 'result' && (
            <>
              <p className="friend-toast-text">
                <strong>{toast.battle.to}</strong>{' '}
                {toast.battle.status === 'accepted' ? t('toastAccepted', { mode: modeText(toast.battle.mode) })
                  : toast.battle.status === 'rejected' ? t('toastRejected') : t('toastExpired')}
              </p>
              {toast.battle.status === 'accepted' && toast.battle.fightId ? (
                <div className="friend-toast-actions">
                  <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => { close(toast.key); openReplay(toast.battle.fightId); }}>{t('watch')}</button>
                </div>
              ) : null}
            </>
          )}
          {toast.type === 'discovery' && (
            <div className="friend-toast-disc">
              {toast.disc.imageUrl ? (
                <img src={`/${String(toast.disc.imageUrl).replace(/^\//, '')}`} alt="" className="friend-toast-disc-img" />
              ) : null}
              <p className="friend-toast-text">
                {t('discoveryUnlocked', { name: toast.disc.name })}
              </p>
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => { close(toast.key); navigate('/wiki'); }}>{t('discoveryWiki')}</button>
            </div>
          )}
          {toast.type === 'friend' && (
            <>
              <p className="friend-toast-text"><strong>{toast.name}</strong> {t('toastFriendRequest')}</p>
              <div className="friend-toast-actions">
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => { close(toast.key); navigate('/profile?tab=friends'); }}>{t('view')}</button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default FriendNotifier;
